/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';

const DelegatorsList = ({ rpcUrl = 'http://localhost:9545' }) => {
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all delegations from all users
        const response = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getAllDelegations',
            params: [],
            id: 1,
          }),
        });
        const data = await response.json();
        
        if (data.result && Array.isArray(data.result)) {
          const delegationsList = data.result
            .filter(delegation => delegation.status === 'Active') // Only active delegations
            .map(delegation => ({
              position_id: delegation.position_id,
              delegator_address: delegation.delegator_address || delegation.address,
              validator_id: delegation.validator_id,
              amount: parseFloat(delegation.amount) || 0,
              rewards: parseFloat(delegation.pending_rewards) || 0,
              staked_at: delegation.staked_at,
              apr: 8, // 8% APR for delegators
              status: delegation.status || 'Active'
            }));
          setDelegations(delegationsList);
        } else {
          setDelegations([]);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setDelegations([]);
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [rpcUrl]);

  const filteredDelegations = delegations.filter(delegation => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      delegation.position_id.toLowerCase().includes(search) ||
      delegation.delegator_address.toLowerCase().includes(search) ||
      delegation.validator_id.toString().includes(search)
    );
  });

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    return `${num.toLocaleString('en-US', { maximumFractionDigits: 2 })} AVO`;
  };

  const formatRewards = (amount) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} AVO`;
  };

  const totalDelegated = delegations.reduce((s, d) => s + parseFloat(d.amount), 0);
  const totalRewards = delegations.reduce((s, d) => s + parseFloat(d.rewards), 0);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      padding: '40px 20px'
    },
    header: {
      maxWidth: '1400px',
      margin: '0 auto 40px',
      textAlign: 'center'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '800',
      color: '#ffffff',
      marginBottom: '16px'
    },
    subtitle: {
      color: '#ffffff',
      fontSize: '1.1rem',
      opacity: 0.8
    },
    statsGrid: {
      maxWidth: '1400px',
      margin: '0 auto 40px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px'
    },
    statCard: {
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      padding: '24px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      cursor: 'default'
    },
    statLabel: {
      fontSize: '0.9rem',
      opacity: 0.8,
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      color: '#ffffff'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      lineHeight: '1.2',
      color: '#ffffff'
    },
    searchContainer: {
      maxWidth: '1400px',
      margin: '0 auto 32px',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '12px',
      padding: '16px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 20px',
      fontSize: '1rem',
      background: '#000000',
      border: '1px solid #ffffff',
      borderRadius: '8px',
      color: '#ffffff',
      outline: 'none'
    },
    tableContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      overflow: 'hidden'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    thead: {
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      borderBottom: '2px solid #ffffff'
    },
    th: {
      padding: '20px',
      textAlign: 'left',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    tr: {
      borderBottom: '1px solid #ffffff',
      transition: 'all 0.2s ease'
    },
    td: {
      padding: '20px',
      color: '#ffffff',
      fontSize: '0.95rem'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    statusActive: {
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #ffffff'
    },
    loadingContainer: {
      maxWidth: '1400px',
      margin: '100px auto',
      textAlign: 'center',
      color: '#ffffff'
    },
    loadingSpinner: {
      fontSize: '4rem',
      marginBottom: '24px',
      animation: 'spin 2s linear infinite'
    },
    emptyState: {
      maxWidth: '1400px',
      margin: '100px auto',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      padding: '60px 40px',
      color: '#ffffff'
    },
    emptyIcon: {
      fontSize: '5rem',
      marginBottom: '24px',
      opacity: 0.6
    },
    codeBlock: {
      background: '#000000',
      border: '1px solid #ffffff',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '24px',
      fontFamily: 'monospace',
      fontSize: '0.9rem',
      color: '#ffffff'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}>⏳</div>
          <h3 style={{ fontSize: '1.5rem', margin: 0 }}>Loading Delegations...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>⚠️</div>
          <h3 style={{ fontSize: '2rem', marginBottom: '16px' }}>Error Loading Delegations</h3>
          <p style={{ fontSize: '1.1rem', opacity: 0.8 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (delegations.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <h3 style={{ fontSize: '2rem', marginBottom: '16px' }}>No Delegations</h3>
          <p style={{ fontSize: '1.1rem', opacity: 0.8, marginBottom: '8px' }}>
            Create your first delegation to get started!
          </p>
          <div style={styles.codeBlock}>
            avo operator delegate to --address YOUR_ADDRESS --validator-id 1 --amount 1000
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🤝 Delegators</h1>
        <p style={styles.subtitle}>
          Delegation positions with 8% APR - Earn rewards by supporting validators
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Delegations</div>
          <div style={styles.statValue}>{delegations.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Delegated</div>
          <div style={styles.statValue}>{formatAmount(totalDelegated)}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Rewards</div>
          <div style={styles.statValue}>{formatRewards(totalRewards)}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Network APR</div>
          <div style={styles.statValue}>8.0%</div>
        </div>
      </div>

      <div style={styles.searchContainer}>
        <input
          type="text"
          style={styles.searchInput}
          placeholder="🔍 Search by Position ID, Delegator Address or Validator ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>Position ID</th>
              <th style={styles.th}>Delegator</th>
              <th style={styles.th}>Validator ID</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>APR</th>
              <th style={styles.th}>Rewards</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredDelegations.map((delegation, index) => (
              <tr 
                key={delegation.position_id} 
                style={{
                  ...styles.tr,
                  background: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <td style={{...styles.td, fontFamily: 'monospace', fontSize: '0.85rem'}}>
                  {delegation.position_id}
                </td>
                <td style={{...styles.td, fontFamily: 'monospace'}}>
                  {delegation.delegator_address.slice(0, 10)}...{delegation.delegator_address.slice(-8)}
                </td>
                <td style={{...styles.td, fontFamily: 'monospace', fontSize: '0.85rem'}}>
                  Validator #{delegation.validator_id}
                </td>
                <td style={{...styles.td, fontWeight: '700', fontSize: '1.1rem'}}>
                  {formatAmount(delegation.amount)}
                </td>
                <td style={{...styles.td, color: '#ffffff', fontWeight: '600'}}>
                  {delegation.apr.toFixed(1)}%
                </td>
                <td style={{...styles.td, color: '#ffffff', fontWeight: '600'}}>
                  {formatRewards(delegation.rewards)}
                </td>
                <td style={styles.td}>
                  <span style={{...styles.statusBadge, ...styles.statusActive}}>
                    {delegation.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        input::placeholder {
          color: rgba(255,255,255,0.6);
        }
      `}</style>
    </div>
  );
};

export default DelegatorsList;
